import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { VehicleGallery } from '@/components/VehicleGallery';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Trophy, 
  Vote, 
  CheckCircle, 
  Loader2, 
  Calendar,
  User,
  Car,
  Star,
  Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CarOfTheMonthPage() {
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(null);
  const [data, setData] = useState(null);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchVotingData();
  }, []);

  const fetchVotingData = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/api/voting/car-of-the-month`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (error) {
      console.error('Error fetching voting data:', error);
      toast.error('Fehler beim Laden der Abstimmung');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (vehicleId, memberId) => {
    setVoting(vehicleId);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/voting/car-of-the-month/vote`,
        { vehicle_id: vehicleId, member_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stimme abgegeben!');
      fetchVotingData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Abstimmen';
      toast.error(msg);
    } finally {
      setVoting(null);
    }
  };

  const handleRemoveVote = async () => {
    setVoting('remove');
    try {
      const token = getToken();
      await axios.delete(
        `${API_URL}/api/voting/car-of-the-month/vote`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stimme zurückgezogen');
      fetchVotingData();
    } catch (error) {
      toast.error('Fehler beim Entfernen der Stimme');
    } finally {
      setVoting(null);
    }
  };

  const getMonthName = () => {
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    const now = new Date();
    return `${months[now.getMonth()]} ${now.getFullYear()}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const candidates = data?.candidates || [];
  const userVote = data?.user_vote;
  const previousWinner = data?.previous_winner;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/20 rounded-full mb-4">
            <Trophy className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-['Oswald'] text-4xl font-bold uppercase tracking-wide mb-2">
            Auto des Monats
          </h1>
          <p className="text-xl text-primary font-medium">{getMonthName()}</p>
          <p className="text-muted-foreground mt-2">
            Stimmen Sie für Ihr Lieblingsfahrzeug unserer Mitglieder
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border p-4 text-center">
            <Car className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{candidates.length}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Fahrzeuge</p>
          </div>
          <div className="bg-card border border-border p-4 text-center">
            <Vote className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{data?.total_votes || 0}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Stimmen</p>
          </div>
          <div className="bg-card border border-border p-4 text-center">
            <User className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{new Set(candidates.map(c => c.owner_id)).size}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Teilnehmer</p>
          </div>
          <div className="bg-card border border-border p-4 text-center">
            {userVote ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-green-500">Abgestimmt</p>
                <p className="text-xs text-muted-foreground truncate">{userVote.vehicle_name}</p>
              </>
            ) : (
              <>
                <Star className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Noch offen</p>
                <p className="text-xs text-muted-foreground">Jetzt abstimmen!</p>
              </>
            )}
          </div>
        </div>

        {/* Previous Winner */}
        {previousWinner && (
          <div className="bg-gradient-to-r from-yellow-500/20 to-primary/20 border border-yellow-500/30 p-6 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-6 h-6 text-yellow-500" />
              <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide">
                Gewinner letzter Monat
              </h2>
            </div>
            <p className="text-xl font-semibold text-foreground">{previousWinner.vehicle_name}</p>
            <p className="text-muted-foreground">von {previousWinner.owner_name} • {previousWinner.count} Stimmen</p>
          </div>
        )}

        {/* User's current vote */}
        {userVote && (
          <div className="bg-green-500/10 border border-green-500/30 p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-foreground">
                  Sie haben für <strong>"{userVote.vehicle_name}"</strong> gestimmt
                </p>
                <p className="text-sm text-muted-foreground">von {userVote.owner_name}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemoveVote}
              disabled={voting === 'remove'}
              className="rounded-none"
            >
              {voting === 'remove' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Stimme ändern'
              )}
            </Button>
          </div>
        )}

        {/* Candidates Grid */}
        {candidates.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border">
            <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              Noch keine Fahrzeuge zur Abstimmung vorhanden
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Fahrzeuge mit mindestens einem Bild können nominiert werden
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {candidates.map((candidate, index) => {
              const isVotedFor = userVote?.vehicle_id === candidate.vehicle_id && 
                               userVote?.member_id === candidate.owner_id;
              const isLeading = index === 0 && candidate.vote_count > 0;
              
              return (
                <div 
                  key={`${candidate.owner_id}-${candidate.vehicle_id}`}
                  className={`bg-card border overflow-hidden transition-all ${
                    isVotedFor 
                      ? 'border-green-500 ring-2 ring-green-500/20' 
                      : isLeading 
                        ? 'border-yellow-500/50'
                        : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`voting-card-${candidate.vehicle_id}`}
                >
                  {/* Leading Badge */}
                  {isLeading && (
                    <div className="bg-yellow-500 text-black px-4 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                      <Crown className="w-4 h-4" />
                      Führend
                    </div>
                  )}
                  
                  {/* Image - Link to detail page */}
                  <Link 
                    to={`/fahrzeug/${candidate.owner_id}/${candidate.vehicle_id}`}
                    className="block p-4 pb-2"
                  >
                    <VehicleGallery 
                      images={candidate.images}
                      vehicleName={candidate.vehicle_name}
                      canEdit={false}
                    />
                  </Link>
                  
                  {/* Info */}
                  <div className="p-4 pt-2 border-t border-border/50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link 
                            to={`/fahrzeug/${candidate.owner_id}/${candidate.vehicle_id}`}
                            className="font-semibold text-lg text-foreground hover:text-primary transition-colors"
                          >
                            {candidate.vehicle_name}
                          </Link>
                          {candidate.vehicle_year && (
                            <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary border border-primary/20">
                              {candidate.vehicle_year}
                            </span>
                          )}
                        </div>
                        {candidate.vehicle_description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {candidate.vehicle_description}
                          </p>
                        )}
                        <p className="text-sm text-primary mt-2 flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {candidate.owner_name}
                        </p>
                      </div>
                      
                      {/* Vote Count & Button */}
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-center">
                          <p className="text-3xl font-bold text-foreground">{candidate.vote_count}</p>
                          <p className="text-xs text-muted-foreground uppercase">
                            {candidate.vote_count === 1 ? 'Stimme' : 'Stimmen'}
                          </p>
                        </div>
                        
                        {!isVotedFor && (
                          <Button
                            onClick={() => handleVote(candidate.vehicle_id, candidate.owner_id)}
                            disabled={voting === candidate.vehicle_id}
                            size="sm"
                            className="rounded-none uppercase tracking-wider"
                          >
                            {voting === candidate.vehicle_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Vote className="w-4 h-4 mr-1" />
                                Stimme
                              </>
                            )}
                          </Button>
                        )}
                        
                        {isVotedFor && (
                          <div className="flex items-center gap-1 text-green-500 text-sm font-medium">
                            <CheckCircle className="w-4 h-4" />
                            Ihre Stimme
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 p-4 bg-muted/30 border border-border">
          <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
            <Star className="w-4 h-4 text-primary" />
            So funktioniert's
          </h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Jedes Mitglied hat eine Stimme pro Monat</li>
            <li>• Sie können nicht für Ihr eigenes Fahrzeug stimmen</li>
            <li>• Nur Fahrzeuge mit Bildern können nominiert werden</li>
            <li>• Am Monatsende wird der Gewinner gekürt</li>
            <li>• Ihre Stimme können Sie jederzeit ändern</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
